/**
 * SHELL — daily landing screen. The zero-signup entry point.
 *
 * Shows today's date, the streak hook (with at-risk emphasis), and the single
 * primary CTA: play today's one attempt — or, if already played, view your
 * result / leaderboard. A practice link is always available (offline-friendly).
 */

import type { App } from '../../app';
import type { Screen } from '../router';
import { div, el } from '../dom';
import { brand, streakBadge, iconButton } from '../components';
import { formatDateLabel, formatCountdown, msUntilUtcMidnight } from '../format';
import { isStreakAtRisk } from '../../storage/local';
import { devToolsActive, devRetryDailyButton } from '../dev';

export function landingScreen(app: App): Screen {
  const snap = app.store.get();
  const screen = div('dr-screen');
  const card = div('dr-card');

  card.append(brand("One run. One course. Everyone, everywhere, today."));
  card.append(el('div', { class: 'dr-date', text: formatDateLabel(snap.dateKey) }));

  const atRisk = isStreakAtRisk(snap.dateKey, snap.streak);
  card.append(streakBadge(snap.streak.current, { atRisk, best: snap.streak.best }));

  if (atRisk) {
    card.append(
      el('p', { class: 'dr-muted', text: `Play today to keep your ${snap.streak.current}-day streak alive.` }),
    );
  }

  const actions = div('dr-btn-row');
  if (snap.dailyStatus === 'completed') {
    actions.append(
      iconButton('dr-btn dr-btn--primary dr-btn--lg dr-btn--block', 'trophy', 'View results', () => app.go('results')),
    );
    screen.append(card, actions);
    screen.append(
      div('dr-btn-row',
        iconButton('dr-btn dr-btn--ghost', 'globe', 'Leaderboard', () => app.go('leaderboard')),
        iconButton('dr-btn dr-btn--ghost', 'play', 'Practice', () => app.go('practice')),
      ),
    );
  } else {
    const cta = iconButton('dr-btn dr-btn--primary dr-btn--lg dr-btn--block', 'play', "Play today's roll", () => {
      void app.startRun();
    });
    cta.setAttribute('autofocus', '');
    actions.append(cta);
    card.append(
      el('p', { class: 'dr-faint', text: 'One attempt. Drag to steer. No sign-up needed.' }),
    );
    screen.append(card, actions);
    screen.append(
      div('dr-btn-row',
        iconButton('dr-btn dr-btn--ghost', 'globe', 'Leaderboard', () => app.go('leaderboard')),
        iconButton('dr-btn dr-btn--ghost', 'play', 'Practice', () => app.go('practice')),
      ),
    );
  }

  // Live countdown to the next daily reset.
  const reset = el('div', { class: 'dr-faint', ariaLive: 'off' });
  const tick = (): void => {
    reset.textContent = `Next roll in ${formatCountdown(msUntilUtcMidnight())}`;
  };
  tick();
  const timer = window.setInterval(tick, 30_000);
  screen.append(reset);

  // DEV ONLY — replay the scored daily run (gated behind the dev flag).
  if (devToolsActive()) {
    screen.append(div('dr-btn-row', devRetryDailyButton(app)));
  }

  return {
    node: screen,
    dispose() {
      window.clearInterval(timer);
    },
  };
}
